"use client";

import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ArrowDown2 } from "iconsax-reactjs";
import * as React from "react";

import { cn } from "@/lib/utils";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(
      "transition-all duration-200 border bg-bg-secondary border-accent-secondary/60 rounded-3xl px-4 mb-3 last:mb-0 [&:has([role=item-content])]:pb-4",
      "data-[state=open]:bg-transparent",
      className,
    )}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

const AccordionItemContent = ({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <AccordionPrimitive.AccordionHeader
    asChild
    role="item-content"
    className={cn(
      "flex items-center gap-2 bg-bg-tertiary/60 px-3 rounded-2xl group",
      "data-[state=open]:bg-bg-elevated data-[state=open]:text-text-primary data-[state=open]:border-stroke-light",
      className,
    )}
    {...props}
  >
    <div>{children}</div>
  </AccordionPrimitive.AccordionHeader>
);
AccordionItemContent.displayName = "AccordionItemContent";

const AccordionTrigger = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex flex-1 items-center justify-between py-4 text-lg font-semibold transition-all hover:underline text-left [&[data-state=open]>svg]:rotate-180",
        className,
      )}
      {...props}
    >
      {children}
      <ArrowDown2
        size={20}
        className="shrink-0 text-text-primary transition-transform duration-200"
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden rounded-sm text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  AccordionItemContent,
};
